import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import ServerCard from "../components/ServerCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, MoreVertical, Pencil, Trash2, Server, Users, Mail, Phone, Bell, HardDrive, Monitor, Copy, RefreshCw, FileSpreadsheet, FileText, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const { getAuthHeader } = useAuth();
  const [clients, setClients] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingServer, setEditingServer] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [usage, setUsage] = useState(null);

  // Form states
  const [clientForm, setClientForm] = useState({
    name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
    alert_on_error: true,
    alert_on_warning: false,
    alert_on_success: false
  });

  const [serverForm, setServerForm] = useState({
    hostname: "",
    ip_address: "",
    os_version: "",
    agent_type: "veeam",
    software_version: "",
    notes: ""
  });

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [clientsRes, serversRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients`, { headers }),
        axios.get(`${API_URL}/api/servers`, { headers }),
        axios.get(`${API_URL}/api/subscription/usage`, { headers }).catch(() => ({ data: null }))
      ]);
      setClients(clientsRes.data);
      setServers(serversRes.data);
      if (usageRes.data) setUsage(usageRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des donnees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Client CRUD
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeader();

    try {
      if (editingClient) {
        await axios.put(`${API_URL}/api/clients/${editingClient.id}`, clientForm, { headers });
        toast.success("Client mis a jour");
      } else {
        await axios.post(`${API_URL}/api/clients`, clientForm, { headers });
        toast.success("Client cree");
      }
      setClientDialogOpen(false);
      resetClientForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteClient = async () => {
    if (!itemToDelete) return;
    const headers = getAuthHeader();

    try {
      await axios.delete(`${API_URL}/api/clients/${itemToDelete.id}`, { headers });
      toast.success("Client supprime");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Server CRUD
  const handleServerSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeader();

    try {
      if (editingServer) {
        await axios.put(`${API_URL}/api/servers/${editingServer.id}`, {
          ...serverForm,
          client_id: editingServer.client_id
        }, { headers });
        toast.success("Serveur mis a jour");
      } else {
        await axios.post(`${API_URL}/api/servers`, {
          ...serverForm,
          client_id: selectedClientId
        }, { headers });
        toast.success("Serveur cree");
      }
      setServerDialogOpen(false);
      resetServerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteServer = async () => {
    if (!itemToDelete) return;
    const headers = getAuthHeader();

    try {
      await axios.delete(`${API_URL}/api/servers/${itemToDelete.id}`, { headers });
      toast.success("Serveur supprime");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetClientForm = () => {
    setClientForm({ 
      name: "", 
      contact_email: "", 
      contact_phone: "", 
      notes: "",
      alert_on_error: true,
      alert_on_warning: false,
      alert_on_success: false
    });
    setEditingClient(null);
  };

  const resetServerForm = () => {
    setServerForm({ 
      hostname: "", 
      ip_address: "", 
      os_version: "", 
      agent_type: "veeam",
      software_version: "", 
      notes: "" 
    });
    setEditingServer(null);
    setSelectedClientId(null);
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name,
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      notes: client.notes || "",
      alert_on_error: client.alert_on_error !== false,
      alert_on_warning: client.alert_on_warning || false,
      alert_on_success: client.alert_on_success || false
    });
    setClientDialogOpen(true);
  };

  const openEditServer = (server) => {
    setEditingServer(server);
    setServerForm({
      hostname: server.hostname,
      ip_address: server.ip_address || "",
      os_version: server.os_version || "",
      agent_type: server.agent_type || "veeam",
      software_version: server.software_version || "",
      notes: server.notes || ""
    });
    setServerDialogOpen(true);
  };

  const openAddServer = (clientId) => {
    setSelectedClientId(clientId);
    resetServerForm();
    setServerDialogOpen(true);
  };

  const getClientServers = (clientId) => servers.filter(s => s.client_id === clientId);

  const getClientStats = (clientId) => {
    const clientServers = getClientServers(clientId);
    return {
      total: clientServers.length,
      success: clientServers.filter(s => s.last_backup_status === "success").length,
      error: clientServers.filter(s => s.last_backup_status === "error").length,
      warning: clientServers.filter(s => s.last_backup_status === "warning").length
    };
  };

  const clientsAtLimit = usage && !usage.unlimited && clients.length >= usage.max_clients;
  const serversAtLimit = usage && !usage.unlimited && servers.length >= usage.max_servers;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Quota bar */}
      {usage && !usage.unlimited && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="usage-quota-bar">
          <div className="p-4 rounded-xl border border-slate-800/60 bg-[#0D111A]/70">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400 flex items-center gap-2"><Users size={14} /> Clients</span>
              <span className={`text-sm font-mono font-semibold ${clientsAtLimit ? 'text-red-400' : 'text-white'}`}>
                {clients.length} / {usage.max_clients}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${clientsAtLimit ? 'bg-red-500' : 'bg-cyan-500'}`}
                style={{ width: `${Math.min(100, (clients.length / usage.max_clients) * 100)}%` }}
              />
            </div>
            {clientsAtLimit && <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Limite atteinte</p>}
          </div>
          <div className="p-4 rounded-xl border border-slate-800/60 bg-[#0D111A]/70">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400 flex items-center gap-2"><Server size={14} /> Serveurs</span>
              <span className={`text-sm font-mono font-semibold ${serversAtLimit ? 'text-red-400' : 'text-white'}`}>
                {servers.length} / {usage.max_servers}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${serversAtLimit ? 'bg-red-500' : 'bg-cyan-500'}`}
                style={{ width: `${Math.min(100, (servers.length / usage.max_servers) * 100)}%` }}
              />
            </div>
            {serversAtLimit && <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Limite atteinte</p>}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Clients</h1>
          <p className="text-slate-400 mt-1">Gerez vos clients et leurs serveurs</p>
        </div>
        <Dialog open={clientDialogOpen} onOpenChange={(open) => {
          setClientDialogOpen(open);
          if (!open) resetClientForm();
        }}>
          <DialogTrigger asChild>
            <Button
              className={clientsAtLimit ? "bg-slate-700 text-slate-400 cursor-not-allowed" : "bg-cyan-600 hover:bg-cyan-500"}
              disabled={clientsAtLimit}
              data-testid="add-client-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              {clientsAtLimit ? "Limite clients atteinte" : "Ajouter un client"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingClient ? "Modifier le client" : "Nouveau client"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleClientSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Nom du client *</Label>
                <Input
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  required
                  className="bg-slate-950 border-slate-700 text-white"
                  data-testid="client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email de contact</Label>
                  <Input
                    type="email"
                    value={clientForm.contact_email}
                    onChange={(e) => setClientForm({ ...clientForm, contact_email: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-white"
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Telephone</Label>
                  <Input
                    value={clientForm.contact_phone}
                    onChange={(e) => setClientForm({ ...clientForm, contact_phone: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-white"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                  className="bg-slate-950 border-slate-700 text-white"
                  data-testid="client-notes-input"
                />
              </div>
              
              {/* Alert preferences */}
              <div className="space-y-3 pt-4 border-t border-slate-700">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Bell size={16} />
                  Preferences d&apos;alertes email
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Alerte sur erreur</span>
                    <Switch
                      checked={clientForm.alert_on_error}
                      onCheckedChange={(checked) => setClientForm({ ...clientForm, alert_on_error: checked })}
                      data-testid="alert-error-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Alerte sur warning</span>
                    <Switch
                      checked={clientForm.alert_on_warning}
                      onCheckedChange={(checked) => setClientForm({ ...clientForm, alert_on_warning: checked })}
                      data-testid="alert-warning-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Alerte sur succes</span>
                    <Switch
                      checked={clientForm.alert_on_success}
                      onCheckedChange={(checked) => setClientForm({ ...clientForm, alert_on_success: checked })}
                      data-testid="alert-success-switch"
                    />
                  </div>
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="save-client-btn">
                {editingClient ? "Enregistrer" : "Creer le client"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Server Dialog */}
      <Dialog open={serverDialogOpen} onOpenChange={(open) => {
        setServerDialogOpen(open);
        if (!open) resetServerForm();
      }}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingServer ? "Modifier le serveur" : "Nouveau serveur"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleServerSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Nom d&apos;hote *</Label>
              <Input
                value={serverForm.hostname}
                onChange={(e) => setServerForm({ ...serverForm, hostname: e.target.value })}
                required
                placeholder="SRV-BACKUP-01"
                className="bg-slate-950 border-slate-700 text-white font-mono"
                data-testid="server-hostname-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Adresse IP</Label>
                <Input
                  value={serverForm.ip_address}
                  onChange={(e) => setServerForm({ ...serverForm, ip_address: e.target.value })}
                  placeholder="192.168.1.100"
                  className="bg-slate-950 border-slate-700 text-white font-mono"
                  data-testid="server-ip-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Type d&apos;agent *</Label>
                <Select 
                  value={serverForm.agent_type} 
                  onValueChange={(value) => setServerForm({ ...serverForm, agent_type: value })}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="agent-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="veeam">
                      <div className="flex items-center gap-2">
                        <Server size={14} className="text-green-400" />
                        Veeam Backup
                      </div>
                    </SelectItem>
                    <SelectItem value="iperius">
                      <div className="flex items-center gap-2">
                        <HardDrive size={14} className="text-orange-400" />
                        Iperius Backup
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Version OS</Label>
                <Input
                  value={serverForm.os_version}
                  onChange={(e) => setServerForm({ ...serverForm, os_version: e.target.value })}
                  placeholder="Windows Server 2022"
                  className="bg-slate-950 border-slate-700 text-white"
                  data-testid="server-os-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Version logiciel</Label>
                <Input
                  value={serverForm.software_version}
                  onChange={(e) => setServerForm({ ...serverForm, software_version: e.target.value })}
                  placeholder="12.0"
                  className="bg-slate-950 border-slate-700 text-white"
                  data-testid="server-software-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={serverForm.notes}
                onChange={(e) => setServerForm({ ...serverForm, notes: e.target.value })}
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="server-notes-input"
              />
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="save-server-btn">
              {editingServer ? "Enregistrer" : "Creer le serveur"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {itemToDelete?.hostname
                ? `Etes-vous sur de vouloir supprimer le serveur "${itemToDelete.hostname}" ? Cette action est irreversible.`
                : `Etes-vous sur de vouloir supprimer le client "${itemToDelete?.name}" et tous ses serveurs ? Cette action est irreversible.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-white hover:bg-slate-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={itemToDelete?.hostname ? handleDeleteServer : handleDeleteClient}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clients list */}
      {clients.length === 0 ? (
        <div className="glass-card rounded-lg p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-slate-500 mb-4" />
          <p className="text-slate-400">Aucun client configure. Ajoutez votre premier client.</p>
        </div>
      ) : (
        <Accordion type="multiple" defaultValue={clients.map(c => c.id)} className="space-y-4">
          {clients.map((client) => {
            const stats = getClientStats(client.id);
            const clientServers = getClientServers(client.id);

            return (
              <AccordionItem
                key={client.id}
                value={client.id}
                className="glass-card rounded-lg border-none"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid={`client-accordion-${client.id}`}>
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Users className="text-purple-400" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-white">{client.name}</h3>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                          {client.contact_email && (
                            <span className="flex items-center gap-1">
                              <Mail size={12} />
                              {client.contact_email}
                            </span>
                          )}
                          {client.contact_phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={12} />
                              {client.contact_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      {/* Stats badges */}
                      <div className="flex items-center gap-3 text-sm">
                        <span className="flex items-center gap-1">
                          <Server size={14} className="text-slate-400" />
                          <span className="text-slate-400">{stats.total}</span>
                        </span>
                        {stats.success > 0 && (
                          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                            {stats.success} OK
                          </span>
                        )}
                        {stats.error > 0 && (
                          <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-xs">
                            {stats.error} Err
                          </span>
                        )}
                        {stats.warning > 0 && (
                          <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
                            {stats.warning} Warn
                          </span>
                        )}
                      </div>
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                            <MoreVertical size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-slate-900 border-slate-700">
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); if (!serversAtLimit) openAddServer(client.id); }}
                            className={serversAtLimit ? "text-slate-600 cursor-not-allowed" : "text-slate-300 hover:text-white focus:text-white"}
                            disabled={serversAtLimit}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {serversAtLimit ? "Limite serveurs atteinte" : "Ajouter un serveur"}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); openEditClient(client); }}
                            className="text-slate-300 hover:text-white focus:text-white"
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemToDelete(client);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-400 hover:text-red-300 focus:text-red-300"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Supprimer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              const headers = getAuthHeader();
                              fetch(`${API_URL}/api/export/clients/${client.id}/csv?days=30`, { headers })
                                .then(r => r.blob())
                                .then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${client.name}_rapport.csv`; a.click(); });
                            }}
                            className="text-slate-300 hover:text-white focus:text-white"
                            data-testid={`export-csv-${client.id}`}
                          >
                            <FileSpreadsheet className="mr-2 h-4 w-4" />
                            Export CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              const headers = getAuthHeader();
                              fetch(`${API_URL}/api/export/clients/${client.id}/pdf?days=30`, { headers })
                                .then(r => r.blob())
                                .then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${client.name}_rapport.pdf`; a.click(); });
                            }}
                            className="text-slate-300 hover:text-white focus:text-white"
                            data-testid={`export-pdf-${client.id}`}
                          >
                            <FileText className="mr-2 h-4 w-4" />
                            Export PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  {clientServers.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Server className="mx-auto h-8 w-8 mb-2 opacity-50" />
                      <p>Aucun serveur pour ce client</p>
                      <Button
                        variant="link"
                        onClick={() => !serversAtLimit && openAddServer(client.id)}
                        disabled={serversAtLimit}
                        className={serversAtLimit ? "text-slate-600 mt-2" : "text-cyan-400 mt-2"}
                      >
                        {serversAtLimit ? "Limite serveurs atteinte" : "Ajouter un serveur"}
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {clientServers.map((server) => (
                        <div key={server.id} className="relative group">
                          <ServerCard server={server} />
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 bg-slate-800/80">
                                  <MoreVertical size={14} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-slate-900 border-slate-700">
                                <DropdownMenuItem
                                  onClick={() => openEditServer(server)}
                                  className="text-slate-300 hover:text-white focus:text-white"
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setItemToDelete(server);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-400 hover:text-red-300 focus:text-red-300"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

export default Clients;
